import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FileSignature, Shield, CheckCircle, XCircle, AlertCircle, Clock,
  Loader2, File, User, Mail, Phone, Download, Eye, Lock, Calendar,
  AlertTriangle, Check, X
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_BASE = `${API_URL}/api`;

// Public API client (no auth required)
const publicApi = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" }
});

export default function PublicSigningPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [step, setStep] = useState("view"); // view, sms, confirm, success, rejected
  const [smsCode, setSmsCode] = useState("");
  const [smsLoading, setSmsLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [certificate, setCertificate] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await publicApi.get(`/signatures/sign/${token}`);
      setData(response.data);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || "Wystąpił błąd";
      
      if (status === 404) {
        setError({ type: "not_found", message: "Link do podpisu nie został znaleziony lub jest nieprawidłowy." });
      } else if (status === 410) {
        setError({ type: "expired", message: detail });
      } else if (status === 400 && detail.includes("już podpisany")) {
        setError({ type: "already_signed", message: "Ten dokument został już przez Ciebie podpisany." });
      } else {
        setError({ type: "error", message: detail });
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDownload = () => {
    window.open(`${API_BASE}/signatures/sign/${token}/file`, "_blank");
  };

  const handleRequestSms = async () => {
    try {
      setSmsLoading(true);
      await publicApi.post(`/signatures/sign/${token}/request-sms`);
      setStep("sms");
    } catch (e) {
      alert(e.response?.data?.detail || "Błąd wysyłania SMS");
    } finally {
      setSmsLoading(false);
    }
  };

  const handleSign = async () => {
    if (!agreed) {
      alert("Musisz zaakceptować oświadczenie, aby podpisać dokument.");
      return;
    }

    try {
      setConfirmLoading(true);
      
      // If SMS required and not yet verified, request SMS first
      if (data.signatory.requireSms && step === "view") {
        await handleRequestSms();
        return;
      }
      
      const payload = {};
      if (data.signatory.requireSms && smsCode) {
        payload.smsCode = smsCode;
      }
      
      const response = await publicApi.post(`/signatures/sign/${token}/confirm`, payload);
      setCertificate(response.data.certificate);
      setStep("success");
    } catch (e) {
      alert(e.response?.data?.detail || "Błąd podpisywania dokumentu");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert("Podaj powód odrzucenia podpisu.");
      return;
    }

    try {
      setConfirmLoading(true);
      await publicApi.post(`/signatures/sign/${token}/reject`, { reason: rejectReason });
      setStep("rejected");
    } catch (e) {
      alert(e.response?.data?.detail || "Błąd odrzucania podpisu");
    } finally {
      setConfirmLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-16 text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-slate-600">Ładowanie dokumentu...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error states
  if (error) {
    const icons = {
      not_found: <AlertTriangle className="h-16 w-16 text-amber-500" />,
      expired: <Clock className="h-16 w-16 text-red-500" />,
      already_signed: <CheckCircle className="h-16 w-16 text-green-500" />,
      error: <XCircle className="h-16 w-16 text-red-500" />
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Helmet>
          <title>Podpis dokumentu — Kurdynowski CRM</title>
        </Helmet>
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <div className="mb-6">{icons[error.type] || icons.error}</div>
            <h2 className="text-xl font-semibold mb-2">
              {error.type === "already_signed" ? "Dokument podpisany" : "Nie można wyświetlić dokumentu"}
            </h2>
            <p className="text-slate-600">{error.message}</p>
            {error.type === "already_signed" && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg text-green-700 text-sm">
                Twój podpis został już złożony. Możesz zamknąć tę stronę.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (step === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Helmet>
          <title>Podpisano pomyślnie — Kurdynowski CRM</title>
        </Helmet>
        <Card className="max-w-lg w-full">
          <CardContent className="py-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-800 mb-2">Dokument podpisany!</h2>
            <p className="text-green-700 mb-6">
              Twój podpis elektroniczny został pomyślnie złożony.
            </p>
            
            {certificate && (
              <div className="bg-white rounded-xl p-6 text-left border border-green-200 mb-6">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-green-600" />
                  Certyfikat podpisu
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">ID certyfikatu:</span>
                    <span className="font-mono text-slate-700">{certificate.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Odcisk palca:</span>
                    <span className="font-mono text-slate-700 text-xs">{certificate.fingerprint?.slice(0, 20)}...</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Data podpisu:</span>
                    <span className="text-slate-700">
                      {new Date(certificate.signedAt).toLocaleString("pl-PL")}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <p className="text-sm text-slate-500">
              Możesz zamknąć tę stronę. Nadawca dokumentu otrzyma powiadomienie o Twoim podpisie.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Rejected state
  if (step === "rejected") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Helmet>
          <title>Podpis odrzucony — Kurdynowski CRM</title>
        </Helmet>
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <X className="h-10 w-10 text-slate-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Podpis odrzucony</h2>
            <p className="text-slate-600 mb-4">
              Odmówiłeś/aś podpisania dokumentu. Nadawca zostanie o tym poinformowany.
            </p>
            <p className="text-sm text-slate-500">
              Możesz zamknąć tę stronę.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main signing view
  const { request, signatory, file } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <Helmet>
        <title>Podpisz dokument: {request.title} — Kurdynowski CRM</title>
      </Helmet>

      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <FileSignature className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Podpis elektroniczny</h1>
          <p className="text-slate-500 mt-1">Kurdynowski CRM</p>
        </div>

        {/* Document Card */}
        <Card className="mb-6 shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">{request.title}</CardTitle>
            <CardDescription>
              Zostałeś/aś zaproszony/a do podpisania tego dokumentu
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Signatory info */}
            <div className="bg-slate-50 rounded-xl p-4">
              <h3 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                <User className="h-4 w-4" />
                Twoje dane
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Imię i nazwisko:</span>
                  <p className="font-medium">{signatory.name}</p>
                </div>
                <div>
                  <span className="text-slate-500">E-mail:</span>
                  <p className="font-medium">{signatory.email}</p>
                </div>
              </div>
            </div>

            {/* File info */}
            <div className="border rounded-xl p-4">
              <h3 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                <File className="h-4 w-4" />
                Dokument do podpisu
              </h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{file?.name || "Dokument"}</p>
                  {file?.size && (
                    <p className="text-sm text-slate-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={handleDownload}>
                  <Download className="h-4 w-4 mr-2" />
                  Pobierz
                </Button>
              </div>
            </div>

            {/* Message from sender */}
            {request.message && (
              <div className="bg-blue-50 rounded-xl p-4">
                <h3 className="font-medium text-blue-800 mb-2">Wiadomość od nadawcy:</h3>
                <p className="text-blue-700">{request.message}</p>
              </div>
            )}

            {/* Expiration */}
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Calendar className="h-4 w-4" />
              Ważność linku: {new Date(request.expiresAt).toLocaleDateString("pl-PL", {
                day: "numeric",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit"
              })}
            </div>

            {/* SMS Verification Step */}
            {step === "sms" && (
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <h3 className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Weryfikacja SMS
                </h3>
                <p className="text-sm text-amber-700 mb-3">
                  Wysłaliśmy kod weryfikacyjny na Twój numer telefonu. Wprowadź go poniżej.
                </p>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Kod SMS"
                    value={smsCode}
                    onChange={(e) => setSmsCode(e.target.value)}
                    maxLength={6}
                    className="flex-1"
                  />
                  <Button 
                    variant="outline" 
                    onClick={handleRequestSms}
                    disabled={smsLoading}
                  >
                    {smsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Wyślij ponownie"}
                  </Button>
                </div>
              </div>
            )}

            {/* Agreement checkbox */}
            {!showRejectForm && (
              <div className="border-t pt-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="agree"
                    checked={agreed}
                    onCheckedChange={setAgreed}
                    className="mt-1"
                  />
                  <Label htmlFor="agree" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
                    Oświadczam, że zapoznałem/am się z treścią dokumentu i wyrażam zgodę na złożenie 
                    podpisu elektronicznego. Rozumiem, że podpis ten ma moc prawną równoważną 
                    podpisowi własnoręcznemu.
                  </Label>
                </div>
              </div>
            )}

            {/* Reject form */}
            {showRejectForm && (
              <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                <h3 className="font-medium text-red-800 mb-3">Odrzuć podpis</h3>
                <Textarea
                  placeholder="Podaj powód odrzucenia..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="mb-3"
                />
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowRejectForm(false)}
                    className="flex-1"
                  >
                    Anuluj
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={handleReject}
                    disabled={confirmLoading}
                    className="flex-1"
                  >
                    {confirmLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Potwierdź odrzucenie
                  </Button>
                </div>
              </div>
            )}
          </CardContent>

          {!showRejectForm && (
            <CardFooter className="flex gap-3 bg-slate-50 rounded-b-xl">
              <Button 
                variant="outline" 
                onClick={() => setShowRejectForm(true)}
                className="flex-1"
                disabled={confirmLoading}
              >
                <X className="h-4 w-4 mr-2" />
                Odmów podpisu
              </Button>
              <Button 
                onClick={handleSign}
                disabled={!agreed || confirmLoading}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {confirmLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                {signatory.requireSms && step === "view" ? "Wyślij kod SMS" : "Podpisz dokument"}
              </Button>
            </CardFooter>
          )}
        </Card>

        {/* Security info */}
        <div className="text-center text-sm text-slate-500">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Lock className="h-4 w-4" />
            Bezpieczne połączenie
          </div>
          <p>
            Podpis elektroniczny jest chroniony certyfikatem cyfrowym i ma moc prawną 
            zgodnie z ustawą o usługach zaufania.
          </p>
        </div>
      </div>
    </div>
  );
}
